// Pure functions for exporting chat sessions to markdown.

import type { ChatSession, ChatFolder, StoredMessage } from '../types';

export type BulkExportScope =
  | { type: 'all' }
  | { type: 'folder'; folderId: string }
  | { type: 'unfiled' };

/** Replace unsafe filename characters. */
export function sanitizeFilename(title: string): string {
  const safe = (title || 'chat')
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
  return safe || 'chat';
}

/** Format a timestamp as YYYY-MM-DD. */
export function formatDate(ts?: number): string {
  const d = ts ? new Date(ts) : new Date();
  return d.toISOString().slice(0, 10);
}

/** Plain markdown: messages only. */
export function chatToMarkdownPlain(session: ChatSession): string {
  const lines: string[] = [];
  lines.push(`# ${session.title || 'Untitled Chat'}`);
  lines.push(`*Exported ${formatDate()} · malpediaRAG*`);
  lines.push('');

  for (const msg of session.messages) {
    lines.push('---');
    lines.push('');
    lines.push(`## ${msg.role === 'user' ? 'User' : 'Assistant'}`);
    lines.push('');
    lines.push(msg.content);
    lines.push('');
  }

  return lines.join('\n');
}

/** Format a single message's metadata as markdown details blocks. */
function formatMessageMetadata(msg: StoredMessage): string {
  const parts: string[] = [];

  // Sources
  if (msg.sources && msg.sources.length > 0) {
    parts.push(`<details>`);
    parts.push(`<summary>Sources (${msg.sources.length})</summary>`);
    parts.push('');
    parts.push('| Cite Key | Score | Title |');
    parts.push('|----------|-------|-------|');
    for (const s of msg.sources) {
      const title = s.title || s.url || '-';
      parts.push(`| ${s.cite_key} | ${s.score.toFixed(3)} | ${title} |`);
    }
    parts.push('');
    parts.push('</details>');
    parts.push('');
  }

  // Pipeline
  const hasPipeline = msg.routingEvent || msg.planningEvent ||
    (msg.queryDetectedIocs && msg.queryDetectedIocs.length > 0) ||
    (msg.responseDetectedIocs && msg.responseDetectedIocs.length > 0);

  if (hasPipeline) {
    parts.push('<details>');
    parts.push('<summary>Pipeline</summary>');
    parts.push('');

    if (msg.routingEvent) {
      parts.push(`- **Mode**: ${msg.routingEvent.mode} (confidence: ${msg.routingEvent.confidence.toFixed(2)})`);
    }
    if (msg.planningEvent) {
      const strats = msg.planningEvent.strategies
        .map((s) => (s.weight != null ? `${s.name}(${s.weight})` : s.name))
        .join(', ');
      const label = msg.planningEvent.source === 'agent' ? 'Tool calls' : 'Strategies';
      parts.push(`- **${label}**: ${strats || 'none'}`);
      if (msg.planningEvent.entities.length > 0) {
        parts.push(`- **Entities**: ${msg.planningEvent.entities.join(', ')}`);
      }
    }
    const allIocs = [
      ...(msg.queryDetectedIocs || []),
      ...(msg.responseDetectedIocs || []),
    ];
    if (allIocs.length > 0) {
      const iocList = allIocs.map((i) => `${i.type}:${i.value}`).join(', ');
      parts.push(`- **IOCs detected**: ${iocList}`);
    }

    parts.push('');
    parts.push('</details>');
    parts.push('');
  }

  return parts.join('\n');
}

/** Detailed markdown: messages + source tables + pipeline metadata. */
export function chatToMarkdownDetailed(session: ChatSession): string {
  const lines: string[] = [];
  lines.push(`# ${session.title || 'Untitled Chat'}`);
  lines.push(`*Exported ${formatDate()} · malpediaRAG*`);
  lines.push('');

  for (const msg of session.messages) {
    lines.push('---');
    lines.push('');
    lines.push(`## ${msg.role === 'user' ? 'User' : 'Assistant'}`);
    lines.push('');
    lines.push(msg.content);
    lines.push('');

    if (msg.role === 'assistant') {
      const meta = formatMessageMetadata(msg);
      if (meta) lines.push(meta);
    }
  }

  return lines.join('\n');
}

/** Bulk export multiple sessions into a single markdown document. */
export function bulkExportToMarkdown(
  sessions: ChatSession[],
  folders: ChatFolder[],
  scope: BulkExportScope,
  detailed: boolean,
): string {
  // Filter sessions by scope
  let filtered: ChatSession[];
  if (scope.type === 'all') {
    filtered = sessions;
  } else if (scope.type === 'folder') {
    filtered = sessions.filter((s) => s.folderId === scope.folderId);
  } else {
    filtered = sessions.filter((s) => !s.folderId);
  }

  const sorted = [...filtered].sort((a, b) => b.updatedAt - a.updatedAt);
  const renderChat = detailed ? chatToMarkdownDetailed : chatToMarkdownPlain;

  const lines: string[] = [];
  lines.push('# Chat Export — malpediaRAG');
  lines.push(`*Exported ${formatDate()} · ${sorted.length} chats*`);
  lines.push('');

  if (scope.type === 'all') {
    // Group by folder
    const folderMap = new Map(folders.map((f) => [f.id, f]));

    for (const folder of folders) {
      const folderSessions = sorted.filter((s) => s.folderId === folder.id);
      if (folderSessions.length === 0) continue;

      lines.push('---');
      lines.push('');
      lines.push(`# 📂 ${folder.name}`);
      lines.push('');

      for (const session of folderSessions) {
        lines.push(renderSingleInBulk(session, detailed));
      }
    }

    // Unfiled
    const unfiled = sorted.filter((s) => !s.folderId || !folderMap.has(s.folderId));
    if (unfiled.length > 0) {
      lines.push('---');
      lines.push('');
      lines.push('# Unfiled');
      lines.push('');
      for (const session of unfiled) {
        lines.push(renderSingleInBulk(session, detailed));
      }
    }
  } else {
    for (const session of sorted) {
      lines.push(renderSingleInBulk(session, detailed));
    }
  }

  return lines.join('\n');
}

/** Render a single session within bulk export (uses h2/h3 headings). */
function renderSingleInBulk(session: ChatSession, detailed: boolean): string {
  const lines: string[] = [];
  const msgCount = session.messages.length;
  lines.push(`## ${session.title || 'Untitled Chat'}`);
  lines.push(`*${formatDate(session.createdAt)} · ${msgCount} messages*`);
  lines.push('');

  for (const msg of session.messages) {
    lines.push(`### ${msg.role === 'user' ? 'User' : 'Assistant'}`);
    lines.push('');
    lines.push(msg.content);
    lines.push('');

    if (detailed && msg.role === 'assistant') {
      const meta = formatMessageMetadata(msg);
      if (meta) lines.push(meta);
    }
  }

  lines.push('---');
  lines.push('');
  return lines.join('\n');
}

/** Trigger a browser file download from a string. */
export function triggerDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
